// Connect Feedback 埋め込み境界（契約 `connect-feedback.embed` v1）のホスト側実装。
//
// 子（Connect Feedback）は信頼しない前提で、受信したメッセージは必ずこのモジュールの
// 検証を通してから処理する。本文・資格情報は契約上やり取りしない（アクセストークンは
// 専用の MessagePort チャネル `connect-feedback.access` だけで渡す）。
//
// 契約の正本は Feedback 側（`meiden-connect/feedback-mcp`）の共有定義であり、本ファイルは
// そのホスト側実装である。Connect Note 固有の記事コンテキスト（articleId / version）は
// 本プロジェクトでは扱わないため、`EmbedContext` は URL だけを持つ。

/** 埋め込み本体の postMessage 契約名。 */
export const EMBED_PROTOCOL = "connect-feedback.embed";
/** アクセストークン供給チャネルの postMessage 契約名。 */
export const ACCESS_PROTOCOL = "connect-feedback.access";
/** Feedback 側へ登録した本プロジェクトのクライアント ID（入口のパスと bootstrap の引数になる）。 */
export const CONNECT_FEEDBACK_CLIENT_ID = "connect-canvas";
/** クライアント ID は入口のパスへ埋め込むため slug に限定する。 */
export const EMBED_CLIENT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/u;
export const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
export const PRODUCT_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/u;
/** 親が受理する内容高さの上限（px）。窓サイズの保存上限と同じ値にそろえる。 */
export const EMBED_CONTENT_HEIGHT_MAX = 4000;
/** メッセージ 1 通の上限（バイト）。巨大なメッセージで親を詰まらせないためのガード。 */
export const EMBED_MESSAGE_BYTES_MAX = 16384;

export interface EmbedProduct {
  readonly productId: string;
  readonly displayName: string;
}

/** 子へ渡すページ情報。本プロジェクトでは URL だけを渡す（入力内容・クエリは渡さない）。 */
export interface EmbedContext {
  readonly url: string;
}

export interface HostState {
  readonly visible: boolean;
  readonly theme: "light" | "dark";
  readonly baseFontPx: number;
  readonly openGeneration: number;
  readonly revision: number;
}

export interface EmbedBootstrap {
  readonly protocolVersion: 1;
  readonly userId: string;
  readonly canCreate: true;
  readonly product: EmbedProduct;
  readonly leaseSeconds: 75;
}

export interface EmbedCapabilities {
  readonly protocol: typeof EMBED_PROTOCOL;
  readonly version: 1;
  readonly enabled: boolean;
  readonly clientId: string;
  readonly hostOrigin: string;
}

type Payloads = {
  hello: Record<string, never>;
  init: HostState & { readonly hostOrigin: string; readonly userId: string };
  "host-state": HostState;
  ready: EmbedBootstrap & { readonly openGeneration: number };
  unavailable: { readonly openGeneration: number };
  verify: { readonly openGeneration: number };
  focus: { readonly openGeneration: number };
  "get-context": { readonly openGeneration: number };
  "context-result": {
    readonly openGeneration: number;
    readonly context: EmbedContext | null;
  };
  minimize: { readonly openGeneration: number };
  activity: { readonly sending: boolean; readonly unread: boolean };
  /**
   * 子が計測した内容の自然な高さ（CSS px）。親はこの値を上限つきで窓の高さへ反映する。
   * 親が設定した iframe の高さに依存しない計測値だけを送る契約のため、循環的な伸縮は起きない。
   */
  "content-size": { readonly openGeneration: number; readonly height: number };
  navigate: {
    readonly openGeneration: number;
    readonly revision: number;
    readonly url: string;
  };
  "navigate-result": { readonly openGeneration: number; readonly ok: boolean };
};

export type EmbedType = keyof Payloads;

export type EmbedMessage = {
  [K in EmbedType]: {
    readonly protocol: typeof EMBED_PROTOCOL;
    readonly version: 1;
    readonly instanceId: string;
    readonly documentNonce: string;
    readonly type: K;
    readonly payload: Payloads[K];
    readonly requestId?: string | undefined;
  };
}[EmbedType];

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** 指定キーを過不足なく持つオブジェクトか。余分なキーの混入を検出するために使う。 */
export function exact(
  value: unknown,
  keys: readonly string[],
  optional: readonly string[] = []
): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    keys.every((key) => Object.hasOwn(value, key)) &&
    Object.keys(value).every((key) => keys.includes(key) || optional.includes(key))
  );
}

export function isUserId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= 128 &&
    !/[\p{Cc}\p{Cf}]/u.test(value)
  );
}

export function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

export function isGeneration(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

/** 内容高さは 1〜上限の整数だけを受理する。0 や小数を通すと窓が潰れたり揺れたりする。 */
export function isEmbedContentHeight(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 1 &&
    value <= EMBED_CONTENT_HEIGHT_MAX
  );
}

/**
 * 完全一致で比較できる origin 文字列か。
 * 既定は https のみ。mock 認証のローカル開発だけ loopback の HTTP も許可する。
 */
export function isEmbedOrigin(value: unknown, allowLoopback = false): value is string {
  if (typeof value !== "string" || value.length > 2048) return false;
  try {
    const url = new URL(value);
    return (
      url.origin === value &&
      !url.username &&
      !url.password &&
      (url.protocol === "https:" ||
        (allowLoopback &&
          url.protocol === "http:" &&
          ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname)))
    );
  } catch {
    return false;
  }
}

/** クエリ・ハッシュ・資格情報を含まない URL か（ページ情報として渡してよい形か）。 */
function isUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 2048) return false;
  try {
    const url = new URL(value);
    return (
      isEmbedOrigin(url.origin, true) && !url.username && !url.password && !url.search && !url.hash
    );
  } catch {
    return false;
  }
}

export function isEmbedContext(value: unknown): value is EmbedContext {
  return exact(value, ["url"]) && isUrl(value.url);
}

function isProduct(value: unknown): value is EmbedProduct {
  return (
    exact(value, ["productId", "displayName"]) &&
    typeof value.productId === "string" &&
    PRODUCT_PATTERN.test(value.productId) &&
    typeof value.displayName === "string" &&
    value.displayName.length > 0 &&
    value.displayName.length <= 200
  );
}

function bootstrapFields(value: Record<string, unknown>): boolean {
  return (
    value.protocolVersion === 1 &&
    isUserId(value.userId) &&
    value.canCreate === true &&
    isProduct(value.product) &&
    value.leaseSeconds === 75
  );
}

export function isEmbedBootstrap(value: unknown): value is EmbedBootstrap {
  return (
    exact(value, ["protocolVersion", "userId", "canCreate", "product", "leaseSeconds"]) &&
    bootstrapFields(value)
  );
}

/** 互換性情報 JSON の形。`enabled` / `clientId` / `hostOrigin` の中身は capabilities.ts が照合する。 */
export function isEmbedCapabilities(value: unknown): value is EmbedCapabilities {
  return (
    exact(value, ["protocol", "version", "enabled", "clientId", "hostOrigin"]) &&
    value.protocol === EMBED_PROTOCOL &&
    value.version === 1 &&
    typeof value.enabled === "boolean" &&
    typeof value.clientId === "string" &&
    EMBED_CLIENT_ID_PATTERN.test(value.clientId) &&
    isEmbedOrigin(value.hostOrigin, true)
  );
}

function stateFields(value: Record<string, unknown>): boolean {
  return (
    typeof value.visible === "boolean" &&
    (value.theme === "light" || value.theme === "dark") &&
    typeof value.baseFontPx === "number" &&
    Number.isFinite(value.baseFontPx) &&
    value.baseFontPx >= 8 &&
    value.baseFontPx <= 48 &&
    isGeneration(value.openGeneration) &&
    isGeneration(value.revision)
  );
}

const STATE_KEYS = ["visible", "theme", "baseFontPx", "openGeneration", "revision"];

/** 契約に適合するメッセージか。受信処理は必ずこの関数を通してから型を信頼する。 */
export function isEmbedMessage(value: unknown): value is EmbedMessage {
  try {
    if (new TextEncoder().encode(JSON.stringify(value)).length > EMBED_MESSAGE_BYTES_MAX) {
      return false;
    }
  } catch {
    // 循環参照など JSON 化できない値は契約外として拒否する。
    return false;
  }
  if (
    !exact(
      value,
      ["protocol", "version", "instanceId", "documentNonce", "type", "payload"],
      ["requestId"]
    ) ||
    value.protocol !== EMBED_PROTOCOL ||
    value.version !== 1 ||
    !isUuid(value.instanceId) ||
    !isUuid(value.documentNonce) ||
    (value.requestId !== undefined && !isUuid(value.requestId))
  ) {
    return false;
  }
  const p = value.payload;
  switch (value.type) {
    case "hello":
      return exact(p, []);
    case "init":
      return (
        exact(p, [...STATE_KEYS, "hostOrigin", "userId"]) &&
        stateFields(p) &&
        isEmbedOrigin(p.hostOrigin, true) &&
        isUserId(p.userId)
      );
    case "host-state":
      return exact(p, STATE_KEYS) && stateFields(p);
    case "ready":
      return (
        exact(p, [
          "protocolVersion",
          "userId",
          "canCreate",
          "product",
          "leaseSeconds",
          "openGeneration",
        ]) &&
        bootstrapFields(p) &&
        isGeneration(p.openGeneration)
      );
    case "unavailable":
    case "verify":
    case "focus":
    case "minimize":
      return exact(p, ["openGeneration"]) && isGeneration(p.openGeneration);
    case "get-context":
      return isUuid(value.requestId) && exact(p, ["openGeneration"]) && isGeneration(p.openGeneration);
    case "context-result":
      return (
        isUuid(value.requestId) &&
        exact(p, ["openGeneration", "context"]) &&
        isGeneration(p.openGeneration) &&
        (p.context === null || isEmbedContext(p.context))
      );
    case "activity":
      return (
        exact(p, ["sending", "unread"]) &&
        typeof p.sending === "boolean" &&
        typeof p.unread === "boolean"
      );
    case "content-size":
      return (
        exact(p, ["openGeneration", "height"]) &&
        isGeneration(p.openGeneration) &&
        isEmbedContentHeight(p.height)
      );
    case "navigate":
      return (
        isUuid(value.requestId) &&
        exact(p, ["openGeneration", "revision", "url"]) &&
        isGeneration(p.openGeneration) &&
        isGeneration(p.revision) &&
        isUrl(p.url)
      );
    case "navigate-result":
      return (
        isUuid(value.requestId) &&
        exact(p, ["openGeneration", "ok"]) &&
        isGeneration(p.openGeneration) &&
        typeof p.ok === "boolean"
      );
    default:
      return false;
  }
}
